<template>
  <div class="min-h-screen bg-black text-white font-sans flex items-center justify-center">
    <div class="w-full max-w-lg px-4">
      <!-- Logo -->
      <div class="flex justify-center mb-4">
        <img src="/icon.png" alt="Ploppin Logo" class="w-12 h-12 rounded-2xl" />
      </div>

      <div class="bg-[#1A2035]/50 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
        <!-- Error Alert -->
        <div v-if="error" class="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-200 text-sm">
          {{ error }}
          
          <!-- Show resend button only for verification errors -->
          <div v-if="showResendOption" class="mt-2">
            <button
              @click="handleResendVerification"
              :disabled="resendLoading"
              class="text-red-200 hover:text-white text-sm underline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="resendLoading">Sending...</span>
              <span v-else>Resend verification email</span>
            </button>
          </div>
        </div>
        
        <!-- Success Alert -->
        <div v-if="successMessage" class="mb-4 p-3 bg-green-500/20 border border-green-500 rounded-lg text-green-200 text-sm">
          {{ successMessage }}
        </div>

        <!-- Title -->
        <h1 class="text-white text-xl font-bold text-center mb-2 lowercase">sign in</h1>
        <p class="text-gray-400 text-center mb-6 lowercase">welcome back!</p>

        <!-- Login Form -->
        <form @submit.prevent="handleSubmit" class="space-y-4">
          <!-- Google Sign In -->
          <button 
            type="button"
            :disabled="isLoading"
            class="w-full flex items-center justify-center gap-3 bg-gray-700 hover:bg-gray-500 text-white rounded-lg p-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            @click="handleGoogleLogin"
          >
            <img src="https://www.google.com/favicon.ico" alt="Google" class="w-4 h-4" />
            Sign in with Google
          </button>

          <div class="relative flex items-center justify-center">
            <div class="border-t border-gray-600 w-full"></div>
            <span class="bg-[#1A2035] text-gray-400 px-4 text-sm">or</span>
            <div class="border-t border-gray-600 w-full"></div>
          </div>

          <!-- Email Input -->
          <div>
            <label class="block text-gray-300 text-sm font-medium mb-1">Email</label>
            <input 
              type="email"
              v-model="email"
              :disabled="isLoading"
              class="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white placeholder-gray-400 focus:outline-none focus:border-gray-400 disabled:opacity-50"
              placeholder="Enter your email"
            />
          </div>

          <!-- Password Input -->
          <div>
            <label class="block text-gray-300 text-sm font-medium mb-1">Password</label>
            <div class="relative">
              <input 
                :type="showPassword ? 'text' : 'password'"
                v-model="password"
                :disabled="isLoading"
                class="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white placeholder-gray-400 focus:outline-none focus:border-gray-400 disabled:opacity-50"
                placeholder="Enter your password"
              />
              <button 
                type="button"
                @click="showPassword = !showPassword"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
              >
                <span v-if="showPassword">👁️</span>
                <span v-else>👁️‍🗨️</span>
              </button>
            </div>
            <!-- Forgot Password Link -->
            <div class="flex justify-end mt-1">
              <NuxtLink to="/forgot-password" class="text-gray-400 hover:text-white text-sm">
                Forgot Password?
              </NuxtLink>
            </div>
          </div>

          <!-- Submit Button -->
          <button 
            type="submit"
            :disabled="isLoading"
            class="w-full bg-gray-700 hover:bg-gray-600 border border-gray-600 py-2.5 rounded-lg flex items-center justify-center gap-2 transition-colors backdrop-blur-sm text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="isLoading">Signing in...</span>
            <span v-else>Sign In</span>
          </button>
        </form>

        <!-- Links -->
        <div class="mt-4 text-center">
          <p class="text-gray-400">
            No account? 
            <NuxtLink to="/register" class="text-gray-400 hover:text-white">Sign Up</NuxtLink>
          </p>
        </div>
      </div>

      <!-- Back to Home -->
      <div class="mt-4 text-center">
        <NuxtLink to="/" class="text-gray-400 hover:text-white transition-colors lowercase">
          ← back to home
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useAuth } from '~/composables/useAuth'

const { loginWithEmail, loginWithGoogle, resendVerificationEmail } = useAuth()

const email = ref('')
const password = ref('')
const showPassword = ref(false)
const isLoading = ref(false)
const error = ref('')
const successMessage = ref('')
const showResendOption = ref(false)
const resendLoading = ref(false)
const route = useRoute()

// Check if we have a verified parameter from email verification
onMounted(() => {
  if (route.query.verified === 'true') {
    successMessage.value = 'Your email has been verified successfully! You can now log in.'
  }
})

const handleGoogleLogin = async () => {
  try {
    error.value = ''
    await loginWithGoogle()
    await navigateTo('/app')
  } catch (err) {
    error.value = err.message || 'Failed to sign in with Google'
  }
}

const handleSubmit = async () => {
  if (!email.value || !password.value) {
    error.value = 'Please fill in all fields'
    return
  }

  try {
    isLoading.value = true
    error.value = ''
    successMessage.value = ''
    showResendOption.value = false
    await loginWithEmail(email.value, password.value)
    await navigateTo('/app')
  } catch (err) {
    if (err.message.includes('not verified')) {
      error.value = 'Please verify your email before signing in. Check your inbox for the verification link.'
      showResendOption.value = true
    } else {
      error.value = err.message || 'Invalid email or password'
      showResendOption.value = false
    }
  } finally {
    isLoading.value = false
  }
}

const handleResendVerification = async () => {
  try {
    resendLoading.value = true
    await resendVerificationEmail(email.value)
    successMessage.value = 'Verification email sent! Please check your inbox.'
    error.value = ''
    showResendOption.value = false
    
    // Re-enable resend after 2 minutes (PocketBase rate limit)
    setTimeout(() => {
      if (!error.value.includes('not verified')) return
      showResendOption.value = true
    }, 120000) // 2 minutes
    
  } catch (err) {
    error.value = err.message || 'Failed to resend verification email'
  } finally {
    resendLoading.value = false
  }
}
</script>